/**
 * propertySource: where did this property come from?
 *
 * ⚠️ ONE FILE, SHARED BY SERVER AND BROWSER, as models/mailability.js is: `functions/models`
 * is a symlink to `src/models`, so the functions `require()` this exact file.
 *
 * WHY IT EXISTS (EST-169, Oct 2026). Until now "has it a jobId?" was asked separately in
 * seven places: the four functions that add to and remove from a mailshot, deleteProperty,
 * the recount (Mailshot.calculateStats) and the mailing export. Everything without a jobId
 * was "manually added", and the export placed all of those together in the file as test
 * addresses. A property brought in by an IMPORT has no jobId either. Left alone, 1,200
 * imported addresses would have been counted as manually added and sent out as test
 * addresses in the middle of a mailing.
 *
 * THE RULE, in this order:
 *   1. it has a `jobId`      → a SCRAPE's      (counted under that scrape)
 *   2. it has an `importId`  → an IMPORT's     (counted under that import)
 *   3. it is a HubSpot quote → HubSpot         (counted as manually added, as it always was)
 *   4. otherwise             → added by hand   (counted as manually added)
 *
 * ⚠️ THE ORDER MATTERS, AND THE RECORD THAT IS BOTH DECIDES IT. A property made by an import
 * can later be saved again by a scrape (only when a scrape runs with "skip existing"
 * unticked). The scrapers then write their own address, status, grade and `jobId` over it
 * and leave the import's marks. From then on it is the SCRAPE's: counted under the scrape,
 * exported in the scrape's run, judged by the scrape's grade, and never deleted by "remove
 * this import". It is still linked to the import (`importId`, `importIds`), which is how the
 * import's page can say "since saved by a scrape".
 *
 * ⚠️ NOTHING HERE CHANGES WHAT AN EXISTING RECORD IS COUNTED AS. Before this file there was
 * no `importId` on any record, so rules 1, 3 and 4 give exactly what the seven places gave:
 * functions/__tests__/propertySourcePlaces.emulator.test.js holds them to a recording made
 * from the code before it. A `jobId` of null or of an empty string is "no scrape", as it was.
 *
 * Tests: src/models/__tests__/propertySource.test.js (the rule),
 * functions/__tests__/propertySource.test.js (the same rule as the functions load it, and
 * that each of the seven places uses it), and the emulator test above (the real functions).
 */

export const SCRAPE = 'scrape';
export const IMPORT = 'import';
export const HUBSPOT = 'hubspot';
export const MANUAL = 'manual';

/**
 * @param {Object} p a discovery_properties document
 * @returns {{ kind: 'scrape'|'import'|'hubspot'|'manual', id: string|null }}
 */
export function sourceOf(p) {
  if (!p) return { kind: MANUAL, id: null };
  if (p.jobId) return { kind: SCRAPE, id: p.jobId };
  if (p.importId) return { kind: IMPORT, id: p.importId };
  if (p.isHubspotImport === true || p.source === HUBSPOT) return { kind: HUBSPOT, id: null };
  return { kind: MANUAL, id: null };
}

/**
 * The mailshot's per-source counter this property moves, as a field path.
 * A HubSpot quote and a hand-added address share "manually added", as they always have.
 */
export function sourceCounterField(p) {
  const s = sourceOf(p);
  if (s.kind === SCRAPE) return `cachedCounts.byScrape.${s.id}`;
  if (s.kind === IMPORT) return `cachedCounts.byImport.${s.id}`;
  return 'cachedCounts.manuallyAdded';
}

/**
 * The per-source part of a full recount. `byImport` is present only when some property
 * came from an import, so a recount of a mailshot with none stores exactly what it did.
 * @returns {{ byScrape: Object, manuallyAdded: number, byImport?: Object }}
 */
export function countBySource(properties) {
  const byScrape = {};
  const byImport = {};
  let manuallyAdded = 0;
  for (const p of properties || []) {
    const s = sourceOf(p);
    if (s.kind === SCRAPE) byScrape[s.id] = (byScrape[s.id] || 0) + 1;
    else if (s.kind === IMPORT) byImport[s.id] = (byImport[s.id] || 0) + 1;
    else manuallyAdded += 1;
  }
  const out = { byScrape, manuallyAdded };
  if (Object.keys(byImport).length > 0) out.byImport = byImport;
  return out;
}

/**
 * The order of the mailing file.
 *
 * Properties arrive in the mailshot's own order. Those of a scrape or of an import form RUNS:
 * a new run starts whenever the source changes from the last scrape-or-import property (so
 * one source can have more than one run). Every other property (a HubSpot quote, an address
 * added by hand, which is how test addresses are made) is held back and placed together:
 * after the run in the middle when there are two or more runs, after the only run when there
 * is one, and alone when there are none.
 *
 * That is what the export did before this file, with "of a scrape" where it now says "of a
 * scrape or of an import". An import's properties used to fall in the held-back group.
 *
 * ⚠️ "The source changes" compares the kind and the id AS THEY ARE STORED, never as text. The
 * export before this file compared the stored jobIds themselves, so a jobId of 5 (a number)
 * and a jobId of "5" were two runs. The first version of this function compared a joined
 * string and made them one, which moved the held-back group in a list that held both (the
 * review of PR #98 found it). A scrape's id is a document id, which is text, so no live
 * record is thought to be like that; the rule is still "as it was" for it.
 *
 * @param {Object[]} properties in the mailshot's own order
 * @returns {{ ordered: Object[], runs: number, heldBack: number }}
 */
export function orderForExport(properties) {
  const runs = [];
  const heldBack = [];
  let current = null;
  let currentKind = null;
  let currentId = null;
  for (const p of properties || []) {
    const s = sourceOf(p);
    if (s.kind !== SCRAPE && s.kind !== IMPORT) { heldBack.push(p); continue; }
    if (current === null || s.kind !== currentKind || s.id !== currentId) {
      current = [];
      runs.push(current);
      currentKind = s.kind;
      currentId = s.id;
    }
    current.push(p);
  }
  const ordered = [];
  if (runs.length === 0) {
    ordered.push(...heldBack);
  } else if (runs.length === 1) {
    ordered.push(...runs[0], ...heldBack);
  } else {
    const middle = Math.floor(runs.length / 2);
    runs.forEach((run, i) => {
      ordered.push(...run);
      if (i === middle) ordered.push(...heldBack);
    });
  }
  return { ordered, runs: runs.length, heldBack: heldBack.length };
}
